import { useState, useEffect } from 'react';
import { Input, Button } from '@predoc/cow';
import { useDebounce } from '@/hooks';
import { useSearchProviderLocations } from '@/feature/patients/hooks/providerAPI';

export type CityLocation = {
  city: string;
  state: string;
};

export type CityAutocompleteProps = {
  value: string;
  onChange: (value: string) => void;
  onCitySelect?: (city: string, state: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minSearchLength?: number;
};

export const CityAutocomplete = ({
  value,
  onChange,
  onCitySelect,
  placeholder = 'ex. Greenville',
  disabled = false,
  className = '',
  minSearchLength = 3,
}: CityAutocompleteProps) => {
  const [query, setQuery] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  const debouncedQuery = useDebounce(query, 300);
  const { data: locationData, isLoading: isLoadingLocations } =
    useSearchProviderLocations(debouncedQuery);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const locations = locationData?.data || [];

  const shouldShowSuggestions =
    isFocused &&
    query.length >= minSearchLength &&
    (locations.length > 0 || isLoadingLocations);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange(newValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleCitySelect = (city: string, state: string) => {
    setQuery(city);
    onChange(city);
    setIsFocused(false);

    if (onCitySelect) {
      onCitySelect(city, state);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <Input
        value={query}
        onChange={handleInputChange}
        type="text"
        placeholder={placeholder}
        disabled={disabled}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {shouldShowSuggestions && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="max-h-60 overflow-auto">
            {isLoadingLocations ? (
              <div className="flex items-center justify-center p-6">
                <div className="text-sm text-gray-600">Searching...</div>
              </div>
            ) : (
              <div className="space-y-1 p-1">
                {locations.map((location, index) => (
                  <Button
                    key={`${location.city}-${location.state}-${index}`}
                    variant="ghost"
                    type="button"
                    className="h-auto w-full justify-start rounded-md p-3 text-left"
                    onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();
                      handleCitySelect(location.city, location.state);
                    }}
                  >
                    <div className="text-sm font-medium text-gray-900">
                      {location.city} - {location.state}
                    </div>
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
